import type { CodiceContatore, Periodo, TipologiaDichiarante, TipologiaImpianto } from '@praticheenergia/domain'
import type { Dec } from './dec'
import type { Formula } from './formula'

export interface ContatorePeriodo {
  readonly id: string
  readonly codice: CodiceContatore
  readonly matricola: string
  /** 0 significa non specificato: impedisce di riconoscere il giro di quadrante. */
  readonly cifreIntere: number
  readonly costanteLettura: Dec
  /** Se vero la lettura del mese contiene gia' i kWh del mese, non un valore cumulativo. */
  readonly kwhDiretti: boolean
  /** Codice J/L/M del tracciato per i contatori C ed E. */
  readonly tipologiaConsumo?: string | undefined
  readonly tipoScambio?: string | undefined
  readonly idControparte?: string | undefined
  readonly pod: readonly string[]
}

/** Valore a fine mese. */
export interface LetturaPeriodo {
  readonly contatoreId: string
  readonly anno: number
  readonly mese: number
  readonly valore: Dec
}

export interface RigoConfigurato {
  readonly quadro: 'J' | 'L' | 'M'
  readonly progr: number
  readonly numeroUtenze: number
  readonly formula: Formula
}

export interface FormulePersonalizzate {
  readonly righi?: readonly RigoConfigurato[] | undefined
  readonly quadroM?: { readonly usiCommerciali: boolean; readonly numeroUtenze: number } | undefined
  readonly perDifferenza?: { readonly quadro: 'C' | 'E'; readonly tipologia: string; readonly formula: Formula } | undefined
  readonly meseInizioAttivita?: number | undefined
}

export interface QuadroSIngresso {
  readonly acconto?: Dec | undefined
  readonly creditoInizio?: Dec | undefined
  readonly debitoInizio?: Dec | undefined
  readonly creditoUtilizzato?: Readonly<Record<number, Dec>> | undefined
  readonly versatoOverride?: Readonly<Record<number, Dec>> | undefined
}

/** quadro -> matricola o progressivo -> mese -> kWh */
export type OverrideKwh = Readonly<Record<string, Readonly<Record<string, Readonly<Record<number, Dec>>>>>>

export interface Elenchi {
  readonly N?: readonly { readonly tipologia: number; readonly id: string; readonly kwh: Dec }[] | undefined
  readonly T?: readonly { readonly provenienza: number; readonly id: string; readonly kwh: Dec }[] | undefined
  readonly perdite?: Dec | undefined
}

/** kWh fatturati da un venditore: niente contatori, una riga per mese,
 * provincia e tipologia di consumo (J1-J7, L1-L9, M1-M12 o M4+M5+M6+M7). */
export interface RigaVendita {
  readonly mese: number
  /** Sigla: l'ambito lo ricavano le regole del periodo, come per l'impianto. */
  readonly provincia: string
  readonly tipologia: string
  readonly numeroUtenze: number
  readonly kwh: Dec
}

export interface DatiPeriodo {
  readonly tipologia: TipologiaImpianto
  readonly tipologiaDichiarante: TipologiaDichiarante
  readonly periodo: Periodo
  readonly annoImposta: number
  /** Sigla della provincia di ubicazione: determina l'ambito. */
  readonly provincia: string
  readonly codiceCatastale?: string | undefined
  readonly contatori: readonly ContatorePeriodo[]
  readonly letture: readonly LetturaPeriodo[]
  readonly formulePersonalizzate: FormulePersonalizzate
  readonly quadroS: QuadroSIngresso
  readonly override: OverrideKwh
  readonly elenchi: Elenchi
  readonly vendite?: readonly RigaVendita[] | undefined
}

export interface MeseDelPeriodo {
  readonly anno: number
  readonly mese: number
}

/** Calendario del periodo, non una regola fiscale: stessa natura del vecchio `mesiAttesi`. */
export function mesiDelPeriodo(
  periodo: Periodo,
  anno: number,
): { readonly precedente: MeseDelPeriodo; readonly mesi: readonly MeseDelPeriodo[] } {
  const primo = periodo === 'semestre_2' ? 7 : 1
  const ultimo = periodo === 'semestre_1' ? 6 : 12
  const mesi = Array.from({ length: ultimo - primo + 1 }, (_, i) => ({ anno, mese: primo + i }))
  const precedente = primo === 1 ? { anno: anno - 1, mese: 12 } : { anno, mese: primo - 1 }
  return { precedente, mesi }
}
